<template>
  <payment-container v-model="_selected" v-bind="containerProps">
    <template v-if="!isStatusExpired">
      <template v-if="isMastercard">
        <img src="~/assets/images/card-types/mastercard.png" class="max-h-8 max-w-10 opacity-75">
      </template>

      <template v-if="isVisa">
        <img src="~/assets/images/card-types/visa.png" class="max-h-8 max-w-14 opacity-75">
      </template>

      <template v-if="isAmex">
        <img src="~/assets/images/card-types/amex.png" class="max-h-8 max-w-14 opacity-75 rounded-sm">
      </template>
    </template>

    <o-chip v-if="isStatusExpired" size="lg" variant="danger" flat>
      {{ $t('general.expired') }}
    </o-chip>

    <o-chip v-if="isStatusExpiringSoon" size="lg" variant="warning" flat>
      {{ $t('general.expiresSoon') }}
    </o-chip>
  </payment-container>
</template>

<script setup lang="ts">
import { v4 as uuid } from 'uuid'

import PaymentContainer from '@/components/order/elements/payments/PaymentContainer.vue'
import { Integration } from '@/_api/models/integration'

const { $api, $i18n } = useNuxtApp()

interface Props {
  order: any
  amount: string | number
  tip?: number | string | null
  option: any
  selected?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  tip: null,
  selected: null,
})

const emit = defineEmits<{
  (e: 'update:selected', value: string): void
}>()

const _selected = computed({
  get: () => props.selected,
  set: (value: string) => emit('update:selected', value),
})

const active = computed(() => props.option.guid === props.selected)

const integration = computed(() => new Integration(props.option.data))

const isMastercard = computed(() => props.option?.data?.cardType?.toLowerCase() === 'mastercard')
const isVisa = computed(() => props.option?.data?.cardType?.toLowerCase() === 'visa')
const isAmex = computed(() => props.option?.data?.cardType?.toLowerCase() === 'americanexpress' || props.option?.data?.cardType?.toLowerCase() === 'amex')
const isStatusActive = computed(() => props.option?.data?.isStatusActive)
const isStatusExpiringSoon = computed(() => props.option?.data?.isStatusExpiringSoon)
const isStatusExpired = computed(() => props.option?.data?.isStatusExpired)

const containerProps = computed(() => {
  let label = props.option.data.cardLast4

  if (isAmex.value) {
    label = $i18n.t('components.order.elements.payments.static.creditCard.amex', { cardLast4: props.option.data.cardLast4 })
  }

  if (isMastercard.value) {
    label = $i18n.t('components.order.elements.payments.static.creditCard.mastercard', { cardLast4: props.option.data.cardLast4 })
  }

  if (isVisa.value) {
    label = $i18n.t('components.order.elements.payments.static.creditCard.visa', { cardLast4: props.option.data.cardLast4 })
  }

  return {
    label,
    info: $i18n.t('components.order.elements.payments.static.creditCard.expires', { cardExpiryMonth: props.option.cardExpiryMonth, cardExpiryYear: props.option.data.cardExpiryYear }),
    option: props.option,
  }
})

async function process() {
  const { amount, option } = props
  const creditCard = option.data
  const idempotency_key = uuid()
  const response = await $api.order.addPaymentSavedCreditCard(String(props.order.idOrder), {
    amount: Number(amount),
    creditCardId: String(creditCard.idCustomerCreditCard),
    idempotencyKey: idempotency_key,
  })
  return response.data
}

async function saturate() {
  const response = await $api.order.get(String(props.order.idOrder))
  return response.data
}

defineExpose({ process })
</script>

